import { AutoTagCandidate, TagInfo } from "@/apis/table/types";
import { createExtendedTag } from "@/apis/taxonomy/route";
import { useExtendForm } from "@/components/mapping/extend/common/forms";
import { useTagging } from "@/hooks/mutations/use-tag";
import useRoleOfCell from "@/hooks/queries/use-role-of-cell";
import { useWorkbookId } from "@/hooks/use-workbook";
import queries from "@/queries";
import { useMappingStore } from "@/stores/mapping";
import { TextChip } from "@/toggle-ui-kit/chip";
import { DataTypes, PeriodTypes, UnitTypes } from "@/types/enums";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Plus, Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { AutoTagProgressPanel } from "../auto-tag-progress/auto-tag-progress-panel";
import {
  axisLabelOf,
  candidateKey,
  labelOf,
  TagLabelMap,
  useApplyCandidate,
  useCellAutoTagInfo,
  useTagLabels,
} from "./auto-tag.hooks";

const TYPE_LABEL: Record<string, string> = {
  LINE_ITEM: "라인아이템",
  MEMBER: "멤버",
};

type Proposal = {
  key: string;
  type: "LINE_ITEM" | "MEMBER";
  koLabel: string;
  enLabel: string;
};

type AxisGroup = { axisKey: string; candidates: AutoTagCandidate[] };

const byRank = (a: AutoTagCandidate, b: AutoTagCandidate) => a.rank - b.rank;

const groupMembersByAxis = (members: AutoTagCandidate[]): AxisGroup[] => {
  const map = new Map<string, AutoTagCandidate[]>();
  members.forEach((c) => {
    const key = c.axisKey ?? "";
    const bucket = map.get(key) ?? [];
    bucket.push(c);
    map.set(key, bucket);
  });
  return Array.from(map.entries()).map(([axisKey, candidates]) => ({
    axisKey,
    candidates: [...candidates].sort(byRank),
  }));
};

/**
 * 매핑탭의 오토태깅 결과 섹션 — AI 후보(1순위 + 대안)를 축별로 보여준다.
 *
 * NoResultActions 아래에 horizontal separator 와 함께 별도 섹션으로 배치된다.
 * Beta feature flag 가 활성일 때만 렌더 (호출 측에서 가드).
 *
 * 데이터는 tagging 컨텍스트의 `auto-tag-info` 엔드포인트(flat candidates)에서 온다.
 * MEMBER 는 axisKey 로 group by 해 1순위(적용됨) + 대안을 보여주고, 분류체계에
 * 존재하는(적용 가능한) 대안을 클릭하면 이 셀에 재태깅한다. 미생성 entity_ 제안은
 * 적용 경로가 없어 노출하지 않는다.
 */
const AutoTagCellCandidates = ({
  onRequestExtendMode,
}: {
  onRequestExtendMode?: (proposal: Proposal) => void;
}) => {
  const { candidates, isAutoTagged } = useCellAutoTagInfo();
  const hasCandidates = isAutoTagged && candidates.length > 0;

  const { apply, applyingKey, appliedKeys } = useApplyCandidate();

  const proposals: Proposal[] = candidates
    .filter((c: any) => c.proposalKoLabel)
    .map((c: any) => ({
      key: c.tagKey,
      type: c.tagType,
      koLabel: c.proposalKoLabel ?? c.tagKey,
      enLabel: c.proposalEnLabel ?? "",
    }));

  const registered = candidates.filter((c: any) => !c.proposalKoLabel);
  const lineItems = registered
    .filter((c: any) => c.tagType === "LINE_ITEM")
    .sort(byRank);
  const memberGroups = groupMembersByAxis(
    registered.filter((c: any) => c.tagType === "MEMBER"),
  );

  // 표시되는 후보 tagKey + 멤버 축 axisKey 의 한국어 라벨을 getTagDetail 로 일괄 조회.
  const labelMap = useTagLabels([
    ...registered.map((c) => c.tagKey),
    ...memberGroups.map((g) => g.axisKey).filter(Boolean),
  ]);

  if (!hasCandidates) return null;
  if (
    proposals.length === 0 &&
    lineItems.length === 0 &&
    memberGroups.length === 0
  )
    return null;

  return (
    <div className="flex w-full p-5 flex-col items-start gap-5 mb-12">
      {(lineItems.length > 0 || memberGroups.length > 0) && (
        <CandidatesSection
          lineItems={lineItems}
          memberGroups={memberGroups}
          labelMap={labelMap}
          apply={apply}
          applyingKey={applyingKey}
          appliedKeys={appliedKeys}
        />
      )}
    </div>
  );
};

/**
 * 오토태깅 뷰 = 진행 표시 + 선택 셀의 후보(셀 오토태깅 시).
 * 진행 패널은 일반 사용자에게도 노출되며, 내부의 debug 전용 세부는 패널이 가드한다.
 */
export const AutoTagSection = (props: {
  onRequestExtendMode?: (proposal: Proposal) => void;
}) => (
  <div className="flex w-full flex-col items-start">
    <AutoTagProgressPanel />
    <AutoTagCellCandidates {...props} />
  </div>
);

const CandidatesSection = ({
  lineItems,
  memberGroups,
  labelMap,
  apply,
  applyingKey,
  appliedKeys,
}: {
  lineItems: AutoTagCandidate[];
  memberGroups: AxisGroup[];
  labelMap: TagLabelMap;
  apply: (candidate: AutoTagCandidate) => void;
  applyingKey: string | null;
  appliedKeys: Set<string>;
}) => {
  return (
    <div className="flex w-full flex-col items-start gap-3">
      <div className="flex items-center gap-2">
        <div
          className="flex items-center justify-center p-2 rounded-[6px] shrink-0"
          style={{ backgroundColor: "#f5f5f5" }}
        >
          <Sparkles className="w-3 h-3" style={{ color: "#171717" }} />
        </div>
        <div className="flex flex-col items-start gap-1">
          <span className="text-[15px] font-semibold text-[#171717] leading-[1.467] tracking-[0.144px]">
            오토태깅
          </span>
          <span className="text-[13px] font-medium text-[#737373] leading-[1.385] tracking-[0.252px]">
            다른 태그를 클릭하여 선택된 셀에 적용합니다.
          </span>
        </div>
      </div>
      <div className="flex flex-col items-start gap-2 w-full min-w-0">
        {lineItems.length > 0 && (
          <CandidateGroup
            type="LINE_ITEM"
            title="라인아이템"
            titlePending={false}
            candidates={lineItems}
            labelMap={labelMap}
            apply={apply}
            applyingKey={applyingKey}
            appliedKeys={appliedKeys}
          />
        )}
        {memberGroups.map((group) => (
          <CandidateGroup
            key={group.axisKey}
            type="MEMBER"
            title={axisLabelOf(labelMap.get(group.axisKey), group.axisKey)}
            titlePending={!!labelMap.get(group.axisKey)?.isLoading}
            candidates={group.candidates}
            labelMap={labelMap}
            apply={apply}
            applyingKey={applyingKey}
            appliedKeys={appliedKeys}
          />
        ))}
      </div>
    </div>
  );
};

const CandidateGroup = ({
  type,
  title,
  titlePending,
  candidates,
  labelMap,
  apply,
  applyingKey,
  appliedKeys,
}: {
  type: "LINE_ITEM" | "MEMBER";
  title: string;
  titlePending: boolean;
  candidates: AutoTagCandidate[];
  labelMap: TagLabelMap;
  apply: (candidate: AutoTagCandidate) => void;
  applyingKey: string | null;
  appliedKeys: Set<string>;
}) => {
  // 노출 기준: 분류체계에 등록(getTagDetail 성공=적용 가능)이거나 조회 중이거나, 이미
  // 적용됐거나, AI 가 실제 적용/생성한 rank1(디폴트). 미생성 entity_ 제안(404)은
  // 적용 경로가 없어 걸러낸다.
  const seenTagKeys = new Set<string>();
  const rows = candidates
    .map((candidate) => ({
      candidate,
      info: labelMap.get(candidate.tagKey),
    }))
    .filter(
      ({ candidate, info }) =>
        info?.isResolved ||
        info?.isLoading ||
        appliedKeys.has(candidate.tagKey) ||
        candidate.rank === 1,
    )
    // 같은 tagKey 가 rank 만 달리해 중복으로 오면(적용본 + 동일 태그 대안 등) 최상위 하나만 남긴다.
    // 한 그룹은 단일 축(MEMBER) 또는 축 없음(LINE_ITEM)이라 tagKey 가 곧 정체성이고,
    // candidates 는 rank 오름차순이라 첫 등장이 최상위다. (candidateKey 는 rank 포함이라 중복 제거 안 됨)
    .filter(({ candidate }) => {
      if (seenTagKeys.has(candidate.tagKey)) return false;
      seenTagKeys.add(candidate.tagKey);
      return true;
    });
  if (rows.length === 0) return null;

  return (
    <div className="flex w-full max-w-full min-w-0 px-3 py-2.5 flex-col items-start gap-2 bg-neutral-50 border border-neutral-200 rounded-[6px] overflow-hidden">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 w-full min-w-0">
        <TextChip
          size="small"
          className="bg-neutral-900 text-white uppercase tracking-wide"
        >
          {TYPE_LABEL[type] ?? type}
        </TextChip>
        {titlePending ? (
          <span className="h-[14px] w-24 rounded bg-neutral-200 animate-pulse" />
        ) : (
          <span
            className="min-w-0 text-[13px] font-semibold text-[#171717] break-words"
            title={title}
          >
            {title}
          </span>
        )}
      </div>
      <div className="flex flex-col items-start gap-1.5 w-full min-w-0">
        {rows.map(({ candidate, info }) => (
          <CandidateRow
            key={candidateKey(candidate)}
            candidate={candidate}
            label={labelOf(info, candidate)}
            // getTagDetail 라벨 조회 중이면 영문 key 대신 스켈레톤(key→ko 깜빡임 방지).
            labelPending={!!info?.isLoading}
            applied={appliedKeys.has(candidate.tagKey)}
            applying={applyingKey === candidateKey(candidate)}
            disabled={applyingKey !== null}
            onApply={() => apply(candidate)}
          />
        ))}
      </div>
    </div>
  );
};

const CandidateRow = ({
  candidate,
  label,
  labelPending,
  applied,
  applying,
  disabled,
  onApply,
}: {
  candidate: AutoTagCandidate;
  label: string;
  labelPending: boolean;
  applied: boolean;
  applying: boolean;
  disabled: boolean;
  onApply: () => void;
}) => {
  return (
    <div
      className={`flex w-full max-w-full min-w-0 px-2.5 py-2 flex-col items-start gap-1 rounded-[6px] border ${
        applied
          ? "bg-neutral-100 border-neutral-300"
          : "bg-white border-neutral-200"
      }`}
    >
      <div className="flex items-center gap-2 w-full min-w-0">
        <span className="shrink-0 text-[11px] font-semibold text-[#a3a3a3]">
          #{candidate.rank}
        </span>
        {labelPending ? (
          <span className="h-[14px] w-28 max-w-[60%] rounded bg-neutral-200 animate-pulse" />
        ) : (
          <span
            className="min-w-0 truncate text-[13px] font-semibold text-[#171717]"
            title={label}
          >
            {label}
          </span>
        )}
        {candidate.extended && (
          <span className="shrink-0 inline-flex items-center rounded-[4px] bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold text-purple-700">
            확장
          </span>
        )}
        {applied ? (
          <span className="ml-auto shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-neutral-500">
            <Check className="w-3 h-3" />
            적용됨
          </span>
        ) : (
          <button
            type="button"
            onClick={onApply}
            disabled={disabled}
            className="ml-auto shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-[#171717] bg-neutral-100 rounded-[4px] hover:bg-neutral-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {applying ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Plus className="w-3 h-3" />
            )}
            적용
          </button>
        )}
      </div>
      <p className="w-full min-w-0 text-[13px] font-medium text-[#525252] leading-[1.5] break-words whitespace-pre-wrap">
        {candidate.reason}
      </p>
    </div>
  );
};

const ProposalChip = ({
  proposal,
  onRequestExtendMode,
}: {
  proposal: Proposal;
  onRequestExtendMode?: (proposal: Proposal) => void;
}) => {
  const { data: workbookId } = useWorkbookId();
  const selectedFact = useMappingStore((state) => state.fact.selected);
  const setDimension = useMappingStore((state) => state.dimension.set);
  const queryClient = useQueryClient();
  const { handleClick } = useTagging({
    toast: {
      onSuccess: () => toast.success("확장 태그가 추가되었습니다."),
      onError: () => toast.error("태그 추가에 실패했습니다."),
    },
  });
  const { patchSearchCacheAsync } = useExtendForm();
  const { data: roleOfCell } = useRoleOfCell({
    workbookId,
    tableId: selectedFact?.table.id,
    cellAddress: selectedFact?.address,
  });

  const [isCreating, setIsCreating] = useState(false);
  const { mutateAsync: createMutate } = useMutation({
    mutationFn: () =>
      createExtendedTag(workbookId!, {
        koLabel: proposal.koLabel,
        enLabel: proposal.enLabel,
        roleKey: roleOfCell?.defRole.key ?? "",
        parentTagKey: roleOfCell?.lineItemKey ?? "",
        cellAddress: selectedFact?.address ?? "",
        tableId: selectedFact?.table.id ?? "",
        type: proposal.type,
        lineItemInfo:
          proposal.type === "LINE_ITEM"
            ? {
                dataType: DataTypes.MONETARY,
                periodType: PeriodTypes.DURATION,
                currency: UnitTypes.KRW,
                balance: null,
                decimals: 0,
                precision: 0,
                negated: false,
              }
            : null,
        childTagKey: null,
        childLineItemInfo: null,
      }),
  });

  const handleClickChip = async () => {
    if (proposal.type === "MEMBER") {
      // axis 선택이 필요해 확장 태그 모드로 이동시킨다. dimension 도 AXIS 로 강제
      // 전환해야 ExtendTagSection 이 MEMBER 확장 폼을 보여준다.
      setDimension("AXIS");
      onRequestExtendMode?.(proposal);
      toast.info(
        `'${proposal.koLabel || proposal.key}' — 축(axis) 을 선택해 확장 태그를 등록해 주세요.`,
      );
      return;
    }
    if (!workbookId || !selectedFact?.table.id || !selectedFact?.address) {
      toast.error("선택된 셀 정보가 없습니다.");
      return;
    }
    if (!roleOfCell?.defRole.key || !roleOfCell?.lineItemKey) {
      toast.error(
        "이 셀의 정의 role/부모 라인아이템 정보가 없어 자동 등록할 수 없습니다.",
      );
      return;
    }
    setIsCreating(true);
    try {
      const created = await createMutate();
      await patchSearchCacheAsync({
        request: {
          address: selectedFact.address,
          dimension: "LINE_ITEM",
          extendedTag: { key: created.key },
        },
      });
      const tagInfo: TagInfo = {
        id: created.id,
        name: created.key.split("_").slice(1).join("_"),
        prefix: created.key.split("_")[0],
        type: "LINE_ITEM",
        label: proposal.koLabel,
        roleKey: roleOfCell.defRole.key,
        parent: null,
      };
      await handleClick(tagInfo, false)();
      await queryClient.invalidateQueries({
        queryKey: queries
          .workbook(workbookId)
          ._ctx.tables(selectedFact.table.id)
          ._ctx.taxonomy().queryKey,
      });
    } catch {
      toast.error("확장 태그 등록에 실패했습니다.");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClickChip}
      disabled={isCreating}
      className="flex max-w-full min-w-0 items-center gap-1.5 px-2.5 py-1.5 bg-neutral-50 border border-neutral-200 rounded-full hover:bg-neutral-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      title={`${proposal.koLabel} (${proposal.key})`}
    >
      <span className="shrink-0 text-[10px] font-semibold uppercase text-white px-1.5 py-0.5 bg-neutral-900 rounded-[4px]">
        {TYPE_LABEL[proposal.type] ?? proposal.type}
      </span>
      <span className="min-w-0 truncate text-[12px] font-semibold text-[#171717]">
        {proposal.koLabel || proposal.key}
      </span>
      {isCreating ? (
        <Loader2 className="w-3 h-3 shrink-0 animate-spin text-neutral-600" />
      ) : (
        <Plus className="w-3 h-3 shrink-0 text-neutral-600" />
      )}
    </button>
  );
};
